import { createReducer, on } from '@ngrx/store';

import { StringPipe } from '@shared/pipes';
import { IPageOfProperty } from '@shared/models/properties';
import { IOrganization } from '@shared/models/organization';

import {
  resetOrganizationAction,
  fetchOrganizationPropertiesAction,
  fetchOrganizationPropertiesActionSuccess,
  fetchOrganizationPropertiesActionFailure,
  setOrganizationUrlSlugAction,
  setLastOrganizationUrlSlugAction,
  getOrganizationByShortNameAction,
  getOrganizationByShortNameActionSuccess,
  getOrganizationByShortNameActionFailure,
} from './organization.actions';

import {
  setPropertyAsFavoriteActionSuccess,
  removePropertyFromFavoritesActionSuccess,
} from '../favorites/favorites.actions';

export interface OrganizationState {
  loadingOrg: boolean;
  properties?: {
    loading: boolean;
    errors: boolean;
    pageOfProperties?: IPageOfProperty;
  };
  orgUrlSlug?: string | null;
  lastOrgUrlSlug?: string | null;
  organization?: IOrganization;
  lastOrganization?: IOrganization;
  orgFetchError: boolean;
}

export const initialState: OrganizationState = {
  properties: undefined,
  loadingOrg: false,
  orgFetchError: false,
};

export const organizationReducer = createReducer(
  initialState,
  on(resetOrganizationAction, () => ({ ...initialState })),

  on(fetchOrganizationPropertiesAction, (state) => ({
    ...state,
    properties: {
      ...state.properties,
      loading: true,
      errors: false,
    },
  })),
  on(fetchOrganizationPropertiesActionSuccess, (state, action) => ({
    ...state,
    properties: {
      ...state.properties,
      loading: false,
      errors: false,
      pageOfProperties: {
        ...action,
        results: [
          ...action.results.map((property) => ({
            ...property,
            price: new StringPipe().transform(
              property.price,
              'strPriceToDecimal'
            ),
          })),
        ],
      },
    },
  })),
  on(fetchOrganizationPropertiesActionFailure, (state) => ({
    ...state,
    properties: {
      ...state.properties,
      loading: false,
      errors: true,
    },
  })),

  on(setPropertyAsFavoriteActionSuccess, (state, action) => ({
    ...state,
    properties: state.properties
      ? {
          ...state.properties,
          pageOfProperties: state.properties.pageOfProperties
            ? {
                ...state.properties.pageOfProperties,
                results: state.properties.pageOfProperties.results.map(
                  (property) => {
                    if (property.id === action.property.id) {
                      return {
                        ...action.property,
                        price: new StringPipe().transform(
                          action.property.price,
                          'strPriceToDecimal'
                        ),
                      };
                    }
                    return property;
                  }
                ),
              }
            : undefined,
        }
      : undefined,
  })),
  on(removePropertyFromFavoritesActionSuccess, (state, action) => ({
    ...state,
    properties: state.properties
      ? {
          ...state.properties,
          pageOfProperties: state.properties.pageOfProperties
            ? {
                ...state.properties.pageOfProperties,
                results: state.properties.pageOfProperties.results.map(
                  (property) => {
                    if (property.id === action.property.id) {
                      return {
                        ...action.property,
                        price: new StringPipe().transform(
                          action.property.price,
                          'strPriceToDecimal'
                        ),
                      };
                    }
                    return property;
                  }
                ),
              }
            : undefined,
        }
      : undefined,
  })),

  on(setOrganizationUrlSlugAction, (state, action) => {
    const newSlug = action.slug?.toLowerCase();
    const currentShortName = state.organization?.shortName?.toLowerCase();
    const shouldResetProperties = newSlug && newSlug !== currentShortName;

    return {
      ...state,
      orgUrlSlug: newSlug,
      properties: shouldResetProperties ? undefined : state.properties,
    };
  }),
  on(setLastOrganizationUrlSlugAction, (state, action) => ({
    ...state,
    lastOrgUrlSlug: action.slug?.toLowerCase(),
  })),

  on(getOrganizationByShortNameAction, (state) => ({
    ...state,
    loadingOrg: true,
    orgFetchError: false,
  })),
  on(getOrganizationByShortNameActionSuccess, (state, action) => ({
    ...state,
    loadingOrg: false,
    orgFetchError: false,
    organization: action.organization,
    lastOrganization:
      action.organization.shortName === state.lastOrgUrlSlug
        ? action.organization
        : state.lastOrganization,
  })),
  on(getOrganizationByShortNameActionFailure, (state) => ({
    ...state,
    loadingOrg: false,
    orgFetchError: true,
  }))
);
